(ns ui.view.transaction-process.view
  (:require
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [frontend.actions.common :as common-actions]
   [frontend.routing :as routing]
   [frontend.view.modals.entity-audit-trail.view :as audit-trail-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing-data]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.transaction-process.components.action-bar :as action-bar]
   [ui.view.transaction-process.components.header-menu :as header-menu]
   [ui.view.transaction-process.components.navigation :as navigation]
   [ui.view.transaction-process.components.permissions-modal :as permissions-modal]
   [ui.components.stepper.view :as stepper]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.pdf-util :as pdf-util]
   [ui.view.transaction-process.cards.details-modal :as details-modal]
   [ui.view.transaction-process.model :as model]
   [ui.view.transaction-process.modals.attestation.view :as attestation-modal]
   [ui.view.transaction-process.modals.declaration.view :as declaration-modal]
   [ui.view.transaction-process.modals.decision.view :as decision-modal]
   [ui.view.transaction-process.stage-renderer :as stage-renderer]
   [ui.view.transaction-process.sub :as sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- status-badge
  [status]
  (when status
    (if-let [label (tp-util/lookup-keyword-label :transaction-process/status status)]
      (let [color (case status
                    :transaction-process.status/active "bg-primary-regular text-primary-regular-text"
                    :transaction-process.status/completed "bg-gray-100 text-gray-600"
                    :transaction-process.status/rejected "bg-red-100 text-red-700"
                    :transaction-process.status/paused "bg-yellow-100 text-yellow-700"
                    :transaction-process.status/archived "bg-gray-100 text-gray-600")]
        [:span {:class (uu/cm "px-3 py-1 rounded text-xs m-0 font-semibold" color)}
         label])
      [tp-util/render-error {:context "Unmapped :transaction-process/status"
                             :value status}])))

(defn- header-bar
  [{:keys [ctx process template stages current-stage dirty? id->entity]}]
  (let [process-id (:process-id ctx)
        read-only? @(rf/subscribe [::sub/effective-read-only?])
        flagged-entities @(rf/subscribe [::session-sub/flagged-entities])
        favorite? (contains? flagged-entities process-id)
        display-name (or (:transaction-process/short-name process)
                         (entity/display-string process)
                         (tr :transaction))]
    [:div {:class "relative z-30 flex shrink-0 flex-wrap xl:flex-nowrap items-center gap-x-4 gap-y-2 min-h-14 px-6 py-3 border-b border-gray-200 bg-white"}
     [:div {:class "order-1 flex min-w-0 flex-1 items-center gap-2"}
      [:button
       {:class    "text-gray-400 hover:text-gray-700 shrink-0"
        :on-click (fn [] (navigation/leave-to-list! dirty?))}
       [:span {:class "text-sm m-0 whitespace-nowrap"} (tr :transaction-processes)]]
      [:span {:class "text-gray-300 m-0 shrink-0"} "/"]
      [:span {:class "text-sm m-0 font-semibold text-gray-900 truncate"} display-name]
      (when dirty?
        [:span {:class "text-xs m-0 text-gray-400 italic ml-2 shrink-0 whitespace-nowrap"} (tr :transaction-process/unsaved)])]
     [:div {:class "order-3 flex w-full min-w-0 items-center justify-center overflow-hidden xl:order-2 xl:w-auto xl:flex-[1.4]"}
      (when stages
        (let [current-idx (.indexOf (mapv :stage/id stages) current-stage)]
          [stepper/view
           {:steps       (mapv (fn [s] {:label (:stage/label s)}) stages)
            :active-step (max 0 current-idx)
            :variant     (if (> (count stages) 7) :with-ellipsis :all-visible)}]))]
     [:div {:class "order-2 flex min-w-0 flex-none items-center justify-end gap-3 xl:order-3 xl:flex-1"}
      [status-badge (:transaction-process/status process)]
      [header-menu/view {:on-permissions     #(rf/dispatch [::event/set-show-permissions ctx true])
                         :on-view-details    #(rf/dispatch [::event/set-show-details ctx true])
                         :on-delete          (when-not read-only?
                                               #(rf/dispatch [::event/delete-process ctx]))
                         :read-only?         read-only?
                         :favorite?          favorite?
                         :on-toggle-favorite #(common-actions/toggle-flag process favorite?)
                         :on-print           (fn []
                                               (pdf-util/create-and-open-pdf
                                                {:entity-data   process
                                                 :template      template
                                                 :current-stage current-stage
                                                 :id->entity    id->entity}))
                         :on-audit-trail     (fn []
                                               (modals/open-modal!
                                                audit-trail-modal/modal-key
                                                {:title               (tr :audit-trail-for [display-name])
                                                 :entity-ids          [process-id]
                                                 :primary-entity-id   process-id
                                                 :primary-entity-type :entity.type/transaction-process}))}]]]))

(defn- modal-renderer
  "Renders the open form modal. The typed `:modal/modal-type` is set by each modal's
  `::open` event alongside its draft; the views read everything else from their
  own subs."
  [{:keys [ctx]}]
  (let [modal @(rf/subscribe [::sub/modal ctx])]
    (when modal
      (case (:modal/modal-type modal)
        (:decision-create :decision-edit)       [decision-modal/view {:ctx ctx}]
        (:attestation-create :attestation-edit) [attestation-modal/view {:ctx ctx}]
        (:declaration-create :declaration-edit) [declaration-modal/view {:ctx ctx}]
        nil))))

(defn- transaction-process-view
  [{:keys [process-id]}]
  (let [ctx {:state-path (model/root-path process-id)
             :process-id process-id}
        dirty-atom (atom false)
        before-unload-handler (fn [e]
                                (when @dirty-atom
                                  (let [msg (tr :transaction-process/unsaved-changes)]
                                    (set! (.-returnValue e) msg)
                                    msg)))]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (rf/dispatch [::event/initialize ctx])
        (.addEventListener js/window "beforeunload" before-unload-handler))
      :component-will-unmount
      (fn [_]
        (.removeEventListener js/window "beforeunload" before-unload-handler)
        (rf/dispatch [::event/cleanup ctx]))
      :reagent-render
      (fn [{:keys [process-id]}]
        (let [process          @(rf/subscribe [::sub/process ctx])
              template         @(rf/subscribe [::sub/template ctx])
              current-stage    @(rf/subscribe [::sub/current-stage ctx])
              stage-config     @(rf/subscribe [::sub/stage-config ctx])
              stages           @(rf/subscribe [::sub/stages ctx])
              transitions      @(rf/subscribe [::sub/transitions ctx])
              dirty?           @(rf/subscribe [::sub/dirty? ctx])
              saving?          @(rf/subscribe [::sub/saving? ctx])
              team-assignments @(rf/subscribe [::sub/team-assignments ctx])
              id->entity        @(rf/subscribe [::session-sub/legacy-entities])
              not-found?       @(rf/subscribe [::sub/not-found? ctx])
              _                (reset! dirty-atom dirty?)
              show-permissions? @(rf/subscribe [::sub/show-permissions? ctx])
              show-details?     @(rf/subscribe [::sub/show-details? ctx])]
          (cond
            not-found?
            [not-found/not-found-view]

            (not process)
            [syncing-data/view]

            :else
            [:div {:class "h-full min-h-0 flex flex-col overflow-hidden bg-gray-50"}
             [header-bar {:ctx           ctx
                          :process       process
                          :template      template
                          :stages        stages
                          :current-stage current-stage
                          :dirty?        dirty?
                          :id->entity    id->entity}]
             (when show-permissions?
               [permissions-modal/view
                {:on-close         #(rf/dispatch [::event/set-show-permissions ctx false])
                 :template         template
                 :team-assignments team-assignments}])
             (when show-details?
               [details-modal/view
                {:on-close    #(rf/dispatch [::event/set-show-details ctx false])
                 :entity-data process
                 :template    template}])
             (if stage-config
               [stage-renderer/view
                {:stage-config     stage-config
                 :process-id       process-id
                 :entity-data      process
                 :template         template
                 :ctx              ctx
                 :current-stage    current-stage
                 :team-assignments team-assignments}]
               [:div {:class "flex-1 flex items-center justify-center text-gray-400 m-0"}
                (tr :transaction-process/no-stage-config)])
             (when transitions
               (let [all-card-fields (->> (:stage/cards stage-config)
                                          (filter #(= :field-card (:card/type %)))
                                          (mapcat :card/fields)
                                          vec)]
                 [action-bar/view
                  {:ctx              ctx
                   :process-id       process-id
                   :current-stage    current-stage
                   :transitions      transitions
                   :template         template
                   :entity-data      process
                   :team-assignments team-assignments
                   :stage-cards      (:stage/cards stage-config)
                   :saving?          saving?
                   :dirty?           dirty?
                   :visible-fields-fn (fn []
                                        @(rf/subscribe [::sub/visible-fields ctx all-card-fields]))
                   :on-save!         (fn [_on-complete]
                                       (rf/dispatch [::event/save-draft ctx]))
                   :on-complete-transaction (fn [field-vals]
                                              (rf/dispatch [::event/complete-transaction ctx field-vals]))}]))
             [modal-renderer {:ctx ctx}]])))})))

(defmethod routing/render-view :entity.type/transaction-process
  [{:keys [route-params]}]
  (let [id-str (:id route-params)]
    (if-not (and (string? id-str) (re-matches #"\d+" id-str))
      [not-found/not-found-view]
      (let [process-id (js/parseInt id-str 10)]
        ^{:key (str "transaction-process-" process-id)}
        [transaction-process-view {:process-id process-id}]))))
